"use client";

import React, {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState
} from "react";
import { signOut } from "firebase/auth";
import {
  Check,
  ChevronRight,
  Clock,
  Copy,
  Lock,
  PlayCircle,
  Plus
} from "lucide-react";
import { toast } from "sonner";
import {
  IconType,
  SiGoogle,
  SiMicrosoft,
  SiOkta
} from "@/components/icons/legacy";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { IconTile } from "@/components/ui/icon-tile";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PageHeader, SectionHeader } from "@/components/ui/page-header";
import { Separator } from "@/components/ui/separator";
import { Spinner } from "@/components/ui/spinner";
import { StatusBadge } from "@/components/ui/status-badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { LawFirmsAPI } from "../api/lawFirms";
import { auth } from "../api/firebaseApi";
import { markAppInitiatedLogout } from "../hooks/useLogOut";
import {
  SsoProviderConfigDTO,
  SsoProviderType,
  SsoSetupStateDTO,
  UpsertSsoProviderRequestDTO
} from "../api/types/lawFirm";
import {
  buildDeterministicSamlSpEntityId,
  getSsoAuthDomain
} from "../helpers/sso";
import { lawyerSelectors } from "../redux/lawyer/selectors";

const showToast = (
  variant: "success" | "error",
  title: string,
  description?: string
) => {
  const notify = variant === "success" ? toast.success : toast.error;
  notify(title, { description, duration: 6000 });
};

const errorMessage = (e: unknown): string => {
  const message =
    typeof e === "object" && e !== null && "message" in e
      ? (e as { message?: unknown }).message
      : undefined;
  return String(message ?? e);
};

type WizardStep = "domains" | "tenant" | "provider" | "provision" | "enable";
type IdpVendor = "okta" | "azure" | "google" | "onelogin" | "custom";

const VENDOR_OPTIONS: Array<{ value: IdpVendor; label: string }> = [
  { value: "okta", label: "Okta" },
  { value: "azure", label: "Microsoft Entra ID" },
  { value: "google", label: "Google Workspace" },
  { value: "onelogin", label: "OneLogin" },
  { value: "custom", label: "Custom" }
];

const VENDOR_ICONS: Partial<Record<IdpVendor, IconType>> = {
  okta: SiOkta as IconType,
  azure: SiMicrosoft as IconType,
  google: SiGoogle as IconType
};

function VendorLogo({
  vendor,
  className = ""
}: {
  vendor: IdpVendor;
  className?: string;
}) {
  if (vendor === "onelogin") {
    return (
      <IconTile size="lg" className={className}>
        1L
      </IconTile>
    );
  }

  const Icon = VENDOR_ICONS[vendor];
  if (!Icon) return null;
  return <IconTile size="lg" icon={Icon} className={className} />;
}

function CopyButton({ value, label }: { value: string; label: string }) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return undefined;
    const id = window.setTimeout(() => setCopied(false), 1500);
    return () => window.clearTimeout(id);
  }, [copied]);

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label={label}
          onClick={() => {
            navigator.clipboard
              .writeText(value)
              .then(() => setCopied(true))
              .catch(() => {});
          }}
        >
          {copied ? <Check /> : <Copy />}
        </Button>
      </TooltipTrigger>
      <TooltipContent>{copied ? "Copied" : "Copy"}</TooltipContent>
    </Tooltip>
  );
}

function CopyValue({
  label,
  value,
  placeholder
}: {
  label: string;
  value: string;
  placeholder?: string;
}) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="flex min-w-0 flex-col gap-0.5">
        <span className="text-sm text-fg-3">{label}</span>
        {value ? (
          <code className="font-mono text-sm break-all text-fg">{value}</code>
        ) : (
          <span className="text-sm text-fg-3">{placeholder}</span>
        )}
      </div>
      {value ? <CopyButton value={value} label={`Copy ${label}`} /> : null}
    </div>
  );
}

function Field({
  id,
  label,
  help,
  children
}: {
  id: string;
  label: string;
  help?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex w-full flex-col gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      {children}
      {help ? (
        <p id={`${id}-help`} className="text-sm text-fg-3">
          {help}
        </p>
      ) : null}
    </div>
  );
}

function LockedNote({ children }: { children: React.ReactNode }) {
  return (
    <p className="flex items-center gap-2 text-base text-fg-3">
      <Lock className="size-3.5" aria-hidden />
      {children}
    </p>
  );
}

function StepSection({
  title,
  description,
  done,
  current,
  locked,
  doneLabel = "Completed",
  onNavigate,
  children
}: {
  title: string;
  description: string;
  done: boolean;
  current: boolean;
  locked: boolean;
  doneLabel?: string;
  /** Set when the step can be opened from here (unlocked and not current). */
  onNavigate?: () => void;
  children: React.ReactNode;
}) {
  return (
    <section className={cn("flex flex-col gap-3", locked && "opacity-60")}>
      <SectionHeader
        title={title}
        description={description}
        actions={
          <>
            {done ? (
              <StatusBadge tone="success">
                <Check />
                {doneLabel}
              </StatusBadge>
            ) : current ? (
              <StatusBadge tone="accent">Current Step</StatusBadge>
            ) : null}
            {onNavigate ? (
              <Button variant="ghost" size="sm" onClick={onNavigate}>
                {done ? "Edit" : "Open"}
              </Button>
            ) : null}
          </>
        }
      />
      <div className="flex flex-col gap-4 rounded-xl bg-elevated p-4 ring-1 ring-line-2">
        {children}
      </div>
    </section>
  );
}

function SettingsColumn({ children }: { children: React.ReactNode }) {
  return (
    <div className="h-full overflow-y-auto bg-surface">
      <div className="mx-auto flex w-full max-w-190 flex-col gap-8 px-12 pt-10 pb-24 mobile:px-4">
        {children}
      </div>
    </div>
  );
}

export function LawFirmSsoSetup() {
  const navigate = useNavigate();
  const fieldId = useId();
  const lawyer = useSelector(lawyerSelectors.selectAll);

  const lawFirmId =
    (lawyer as { law_firm_id?: string | null })?.law_firm_id || null;
  const role = String((lawyer as { role?: string | null })?.role || "")
    .trim()
    .toLowerCase();
  const canManageSso =
    role === "owner" || role === "admin" || role === "superadmin";

  const [isLoading, setIsLoading] = useState(false);
  const [state, setState] = useState<SsoSetupStateDTO | null>(null);
  const [providerConfigs, setProviderConfigs] = useState<
    SsoProviderConfigDTO[]
  >([]);

  const [wizardStep, setWizardStep] = useState<WizardStep>("domains");
  const [selectedVendor, setSelectedVendor] = useState<IdpVendor | null>(null);
  const [hasProvisioned, setHasProvisioned] = useState(false);

  const [domainsText, setDomainsText] = useState("");

  // Provider form
  const [providerType, setProviderType] = useState<SsoProviderType>("oidc");
  const [providerId, setProviderId] = useState("");
  const [providerLabel, setProviderLabel] = useState("");

  // OIDC fields
  const [oidcIssuer, setOidcIssuer] = useState("");
  const [oidcClientId, setOidcClientId] = useState("");
  const [oidcClientSecret, setOidcClientSecret] = useState("");

  // SAML fields (basic)
  const [samlIdpEntityId, setSamlIdpEntityId] = useState("");
  const [samlSsoUrl, setSamlSsoUrl] = useState("");
  const [samlCert, setSamlCert] = useState("");
  const [samlMetadataUrl, setSamlMetadataUrl] = useState("");
  const [samlBindingPreference, setSamlBindingPreference] = useState<
    "redirect" | "post"
  >("redirect");
  const [samlCertFileName, setSamlCertFileName] = useState<string | null>(null);
  const samlCertFileRef = useRef<HTMLInputElement | null>(null);

  const effectiveProviderId = useMemo(() => {
    const vendor = String(selectedVendor || "")
      .trim()
      .toLowerCase();
    if (vendor && vendor !== "custom") {
      return `${providerType}.${vendor}`;
    }
    return String(providerId || "").trim();
  }, [providerId, providerType, selectedVendor]);

  const hasSelectedVendor = Boolean(selectedVendor);

  const refresh = useCallback(async () => {
    if (!lawFirmId) return;
    setIsLoading(true);
    try {
      const payload = await LawFirmsAPI.getSsoSetup(lawFirmId);
      setState(payload);
      setDomainsText(String((payload.allowedEmailDomains ?? [])[0] ?? ""));
      if (payload.tenantId && (payload.providers?.length ?? 0) > 0) {
        try {
          const configPayload = await LawFirmsAPI.getSsoProviderConfigs(
            lawFirmId
          );
          setProviderConfigs(
            Array.isArray(configPayload.configs) ? configPayload.configs : []
          );
        } catch {
          setProviderConfigs([]);
        }
      } else {
        setProviderConfigs([]);
      }
    } finally {
      setIsLoading(false);
    }
  }, [lawFirmId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    // Heuristic: keep wizard step aligned to current setup state.
    const domainsDone = (state?.allowedEmailDomains ?? []).length === 1;
    const tenantDone = Boolean(state?.tenantId);
    const providerDone = (state?.providers?.length ?? 0) > 0;
    const provisionDone = hasProvisioned;

    const recommended: WizardStep = !domainsDone
      ? "domains"
      : !tenantDone
      ? "tenant"
      : !providerDone
      ? "provider"
      : !provisionDone
      ? "provision"
      : "enable";

    const order: WizardStep[] = [
      "domains",
      "tenant",
      "provider",
      "provision",
      "enable"
    ];
    const currentIdx = order.indexOf(wizardStep);
    const maxIdx = order.indexOf(recommended);

    // Allow navigating to completed steps, but block future/locked steps.
    if (currentIdx > maxIdx) {
      setWizardStep(recommended);
    }
  }, [
    state?.allowedEmailDomains,
    state?.tenantId,
    state?.providers?.length,
    hasProvisioned,
    wizardStep
  ]);

  const authDomain = useMemo(() => getSsoAuthDomain(), []);

  const authHandlerUrl = useMemo(() => {
    return `https://${authDomain}/__/auth/handler`;
  }, [authDomain]);

  const appLoginUrl = useMemo(() => {
    const { origin } = window.location;
    return `${origin}/login`;
  }, []);

  const normalizedDomains = useMemo(() => {
    const first = domainsText
      .split(/\r?\n|,/g)
      .map((d) => d.trim().toLowerCase())
      .map((d) => (d.startsWith("@") ? d.slice(1) : d))
      .filter(Boolean);
    return first.length ? [first[0]] : [];
  }, [domainsText]);

  const configuredProvider = useMemo(() => {
    return ((state?.providers ?? [])[0] ?? null) || null;
  }, [state?.providers]);

  const configuredProviderConfig = useMemo(() => {
    const configuredProviderId = String(
      configuredProvider?.provider_id || ""
    ).trim();
    if (!configuredProviderId) return null;
    return (
      providerConfigs.find(
        (config) =>
          String(config.provider_id || "").trim() === configuredProviderId
      ) ?? null
    );
  }, [configuredProvider, providerConfigs]);

  const configuredProviderType = String(
    configuredProviderConfig?.type || configuredProvider?.type || ""
  )
    .trim()
    .toLowerCase();
  const configuredProviderId = String(
    configuredProvider?.provider_id || ""
  ).trim();

  const samlCallbackUrl = String(
    configuredProviderConfig?.callbackUri || authHandlerUrl
  ).trim();
  const previewSamlSpEntityId = useMemo(() => {
    if (providerType !== "saml" || !lawFirmId || !effectiveProviderId) {
      return "";
    }

    return buildDeterministicSamlSpEntityId({
      authDomain,
      lawFirmId,
      providerId: effectiveProviderId
    });
  }, [authDomain, effectiveProviderId, lawFirmId, providerType]);

  const configuredSamlSpEntityId = useMemo(() => {
    if (!lawFirmId || !configuredProviderId) {
      return "";
    }

    return buildDeterministicSamlSpEntityId({
      authDomain,
      lawFirmId,
      providerId: configuredProviderId
    });
  }, [authDomain, configuredProviderId, lawFirmId]);

  const previewSamlSpEntityIdMessage =
    previewSamlSpEntityId ||
    "Select a provider and finalize the provider ID to generate this value.";

  const itInstructionsText = useMemo(() => {
    const audienceBlock =
      configuredProviderType === "saml"
        ? `\n\n2) SP Entity ID / Audience URI:\n${configuredSamlSpEntityId}`
        : "";
    const appLoginNumber = configuredProviderType === "saml" ? "3" : "2";
    const domainsNumber = configuredProviderType === "saml" ? "4" : "3";
    const domainsForInstructions = normalizedDomains.length
      ? normalizedDomains
      : ["(none set)"];

    return `SSO setup for ${
      state?.lawFirmName ?? "Law firm"
    }\n\n1) Redirect / callback URL (SAML ACS or OIDC redirect URI):\n${
      configuredProviderType === "saml" ? samlCallbackUrl : authHandlerUrl
    }${audienceBlock}\n\n${appLoginNumber}) App login URL (where users start):\n${appLoginUrl}\n\n${domainsNumber}) Allowed email domains:\n${domainsForInstructions.join(
      "\n"
    )}\n\nNotes:\n- Ensure the IdP releases a reliable email attribute/claim.\n- Invite required for new users (no auto-provision).\n- CaseBlink defines the SAML Audience URI shown above.`;
  }, [
    appLoginUrl,
    authHandlerUrl,
    configuredSamlSpEntityId,
    configuredProviderType,
    normalizedDomains,
    samlCallbackUrl,
    state?.lawFirmName
  ]);

  const handleSaveDomains = async () => {
    if (!lawFirmId) return;
    setIsLoading(true);
    try {
      await LawFirmsAPI.updateSsoAllowedEmailDomains(lawFirmId, {
        allowed_email_domains: Array.from(new Set(normalizedDomains))
      });
      showToast(
        "success",
        "Domains saved",
        "Your allowed email domain has been updated for SSO sign-in."
      );
      await refresh();
      setWizardStep("tenant");
    } catch (e: unknown) {
      showToast("error", "Failed to save domains", errorMessage(e));
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateTenant = async () => {
    if (!lawFirmId) return;
    setIsLoading(true);
    try {
      await LawFirmsAPI.createTenant(lawFirmId, null);
      showToast(
        "success",
        "Tenant created",
        "Your firm now has a dedicated tenant."
      );
      await refresh();
      setWizardStep("provider");
    } catch (e: unknown) {
      showToast("error", "Failed to create tenant", errorMessage(e));
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpsertProvider = async () => {
    if (!lawFirmId) return;
    setIsLoading(true);
    try {
      const req: UpsertSsoProviderRequestDTO = {
        provider_id: effectiveProviderId,
        type: providerType,
        label: providerLabel.trim() || null,
        enabled: true,
        config:
          providerType === "oidc"
            ? {
                issuer: oidcIssuer.trim(),
                clientId: oidcClientId.trim(),
                clientSecret: oidcClientSecret
              }
            : {
                idpConfig: {
                  idpEntityId: samlIdpEntityId.trim(),
                  ssoUrl: samlSsoUrl.trim(),
                  idpCertificates: [{ x509Certificate: samlCert.trim() }]
                },
                spConfig: {
                  callbackUri: authHandlerUrl
                }
              }
      };

      await LawFirmsAPI.upsertSsoProvider(lawFirmId, req);
      showToast(
        "success",
        "Provider saved",
        "Your identity provider configuration is now available for validation."
      );
      await refresh();
      setWizardStep("provision");
    } catch (e: unknown) {
      showToast("error", "Failed to save provider", errorMessage(e));
    } finally {
      setIsLoading(false);
    }
  };

  const handleProvision = async (dryRun: boolean) => {
    if (!lawFirmId) return;
    setIsLoading(true);
    try {
      const report = await LawFirmsAPI.provisionSsoUsers(lawFirmId, {
        dry_run: dryRun,
        limit: 500
      });
      showToast(
        "success",
        dryRun ? "Dry-run complete" : "Provisioning complete",
        `Processed ${report.total} users`
      );
      if (!dryRun) setHasProvisioned(true);
      if (!dryRun) setWizardStep("enable");
    } catch (e: unknown) {
      showToast("error", "Provisioning failed", errorMessage(e));
    } finally {
      setIsLoading(false);
    }
  };

  const handleImportSamlMetadata = async () => {
    if (!lawFirmId) return;
    setIsLoading(true);
    try {
      const resp = await LawFirmsAPI.importSamlMetadata(lawFirmId, {
        metadata_url: samlMetadataUrl.trim(),
        binding_preference: samlBindingPreference
      });
      setSamlIdpEntityId(resp.idpEntityId || "");
      setSamlSsoUrl(resp.ssoUrl || "");
      if (resp.x509Certificate) {
        setSamlCert(resp.x509Certificate);
        setSamlCertFileName("metadata.xml");
      }
      showToast(
        "success",
        "Metadata imported",
        "We filled in the SAML fields using your metadata document."
      );
    } catch (e: unknown) {
      showToast("error", "Failed to import metadata", errorMessage(e));
    } finally {
      setIsLoading(false);
    }
  };

  const handleUploadCertFile = async (file: File | null) => {
    if (!file) return;
    const text = await file.text();
    setSamlCert(text);
    setSamlCertFileName(file.name);
  };

  const handleEnable = async () => {
    if (!lawFirmId) return;
    if (isLoading) return;
    setIsLoading(true);
    try {
      await LawFirmsAPI.enableSso(lawFirmId);
      showToast(
        "success",
        "SSO enabled",
        "You’ll be signed out now. Please sign in again using SSO."
      );

      // Once SSO is enabled, backend tenant enforcement will reject non-tenant tokens.
      // Sign out and redirect to the SSO test login flow for a clean transition.
      const email = String((lawyer as { email?: string | null })?.email || "")
        .trim()
        .toLowerCase();
      const nextUrl = email
        ? `/login?sso_test=1&email=${encodeURIComponent(email)}`
        : "/login?sso_test=1";
      try {
        markAppInitiatedLogout();
        await signOut(auth);
      } catch {
        // Ignore logout failures; we still hard-navigate to restart auth flow.
      }
      window.location.assign(nextUrl);
    } catch (e: unknown) {
      showToast("error", "Failed to enable SSO", errorMessage(e));
    } finally {
      setIsLoading(false);
    }
  };

  if (!lawFirmId) {
    return (
      <SettingsColumn>
        <PageHeader
          title="SSO Setup"
          description="Could not determine your law firm. Please contact support."
        />
        <div>
          <Button onClick={() => navigate("/cases")}>Back</Button>
        </div>
      </SettingsColumn>
    );
  }

  if (!canManageSso) {
    return (
      <SettingsColumn>
        <PageHeader
          title="SSO Setup"
          description="You don’t have permission to configure SSO for this firm."
        />
        <div>
          <Button onClick={() => navigate("/cases")}>Back</Button>
        </div>
      </SettingsColumn>
    );
  }

  const domainsDone = (state?.allowedEmailDomains ?? []).length === 1;
  const tenantDone = Boolean(state?.tenantId);
  const providerDone = (state?.providers?.length ?? 0) > 0;
  const provisionDone = hasProvisioned;
  const enableDone = Boolean(state?.ssoEnabled);

  const progressOrder: WizardStep[] = [
    "domains",
    "tenant",
    "provider",
    "provision",
    "enable"
  ];

  const maxUnlockedStep: WizardStep = !domainsDone
    ? "domains"
    : !tenantDone
    ? "tenant"
    : !providerDone
    ? "provider"
    : !provisionDone
    ? "provision"
    : "enable";

  const highestAccessibleIdx = enableDone
    ? progressOrder.length - 1
    : progressOrder.indexOf(maxUnlockedStep);

  const canNavigateTo = (step: WizardStep): boolean => {
    return progressOrder.indexOf(step) <= highestAccessibleIdx;
  };

  const goToStep = (step: WizardStep) => {
    if (!canNavigateTo(step)) return;
    setWizardStep(step);
  };

  const handleValidateConfiguration = async () => {
    if (!lawFirmId || !configuredProvider) return;
    if (isLoading) return;

    setIsLoading(true);
    try {
      const providerType = String(configuredProvider.type || "")
        .trim()
        .toLowerCase();
      const response = await LawFirmsAPI.validateSsoProvider(lawFirmId, {
        provider_id: String(configuredProvider.provider_id || "").trim(),
        provider_type: providerType === "saml" ? "saml" : "oidc",
        expected_callback_uri: authHandlerUrl
      });
      const failedCheck = (response.checks || []).find((check) => !check.ok);

      if (response.ok) {
        showToast(
          "success",
          "Validation passed",
          "Your SSO configuration checks completed successfully."
        );
      } else {
        showToast(
          "error",
          "Validation failed",
          failedCheck?.details || "One or more configuration checks failed."
        );
      }
    } catch (e: unknown) {
      showToast(
        "error",
        "Validation failed",
        errorMessage(e ?? "Unknown error")
      );
    } finally {
      setIsLoading(false);
    }
  };

  const navigateTo = (step: WizardStep) =>
    wizardStep !== step && canNavigateTo(step)
      ? () => goToStep(step)
      : undefined;

  const loadingIcon = <Spinner data-icon="inline-end" className="size-3.5" />;
  const continueIcon = isLoading ? (
    loadingIcon
  ) : (
    <ChevronRight data-icon="inline-end" />
  );

  const domainId = `${fieldId}-domain`;
  const providerIdFieldId = `${fieldId}-provider-id`;
  const labelId = `${fieldId}-label`;
  const issuerId = `${fieldId}-issuer`;
  const clientIdId = `${fieldId}-client-id`;
  const clientSecretId = `${fieldId}-client-secret`;
  const metadataUrlId = `${fieldId}-metadata-url`;
  const idpEntityIdId = `${fieldId}-idp-entity-id`;
  const ssoUrlId = `${fieldId}-sso-url`;
  const certId = `${fieldId}-cert`;

  const redirectUrl =
    configuredProviderType === "saml" ? samlCallbackUrl : authHandlerUrl;
  const isSamlConfigured = configuredProviderType === "saml";

  return (
    <TooltipProvider>
      <SettingsColumn>
        <PageHeader
          title="SSO Setup"
          description={
            state?.lawFirmName
              ? `Configure single sign-on for ${state.lawFirmName}.`
              : "Configure single sign-on for your firm"
          }
          actions={
            <StatusBadge tone={enableDone ? "success" : "warn"} dot>
              {enableDone ? "SSO Enabled" : "Configuration in Progress"}
            </StatusBadge>
          }
        />

        {/* Step 1: Domains */}
        <StepSection
          title="Step 1: Email Domain"
          description="Add the single email domain that should use SSO for this firm."
          done={domainsDone}
          current={wizardStep === "domains"}
          locked={false}
          onNavigate={navigateTo("domains")}
        >
          {wizardStep === "domains" ? (
            <>
              <Field
                id={domainId}
                label="Allowed Email Domain"
                help="Example: firm.com"
              >
                <Input
                  id={domainId}
                  aria-describedby={`${domainId}-help`}
                  placeholder="firm.com"
                  value={domainsText}
                  onChange={(e) => setDomainsText(e.target.value)}
                />
              </Field>

              <div className="flex items-center justify-end gap-2">
                <Button onClick={() => navigate("/cases")}>Cancel</Button>
                <Button
                  variant="primary"
                  disabled={isLoading || normalizedDomains.length !== 1}
                  onClick={() => handleSaveDomains().catch(() => {})}
                >
                  Save and Continue
                  {continueIcon}
                </Button>
              </div>
            </>
          ) : (
            <div className="flex flex-col gap-1">
              <span className="text-sm text-fg-3">Allowed Domains</span>
              <span className="text-base text-fg">
                {(state?.allowedEmailDomains ?? []).join(", ") || "—"}
              </span>
            </div>
          )}
        </StepSection>

        {/* Step 2: Tenant */}
        <StepSection
          title="Step 2: Create Tenant"
          description="Create the law firm tenant"
          done={tenantDone}
          current={wizardStep === "tenant"}
          locked={!domainsDone && wizardStep !== "tenant"}
          onNavigate={navigateTo("tenant")}
        >
          {!domainsDone && wizardStep !== "tenant" ? (
            <LockedNote>Complete Step 1 to continue</LockedNote>
          ) : wizardStep === "tenant" ? (
            <>
              <p className="text-base text-fg-3">
                Create an isolated tenant for your organization. This enables
                isolated authentication for your firm.
              </p>
              <div className="flex items-center justify-between gap-4 rounded-lg bg-fill-1 px-3 py-2.5 mobile:flex-col mobile:items-start">
                <div className="flex flex-col gap-0.5">
                  <span className="text-sm text-fg-3">Tenant</span>
                  <span className="text-base text-fg">
                    {tenantDone ? "Created" : "Not yet created"}
                  </span>
                </div>
                <Button
                  variant="primary"
                  disabled={isLoading || tenantDone}
                  onClick={() => handleCreateTenant().catch(() => {})}
                >
                  {isLoading ? <Spinner className="size-3.5" /> : <Plus />}
                  Create Tenant
                </Button>
              </div>
            </>
          ) : (
            <span className="text-base text-fg">
              {tenantDone ? "Tenant created" : "Not yet created"}
            </span>
          )}
        </StepSection>

        {/* Step 3: Provider */}
        <StepSection
          title="Step 3: Configure Provider"
          description="OIDC or SAML configuration"
          done={providerDone}
          current={wizardStep === "provider"}
          locked={!tenantDone && wizardStep !== "provider"}
          onNavigate={navigateTo("provider")}
        >
          {!tenantDone && wizardStep !== "provider" ? (
            <LockedNote>Complete Step 2 to continue</LockedNote>
          ) : wizardStep === "provider" ? (
            <>
              <div
                role="radiogroup"
                aria-label="Identity Provider"
                className="flex flex-wrap gap-2"
              >
                {VENDOR_OPTIONS.map((option) => {
                  const selected = selectedVendor === option.value;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      onClick={() => setSelectedVendor(option.value)}
                      className={cn(
                        "flex min-h-12 min-w-36 flex-1 items-center gap-3 rounded-lg px-2 text-left inset-ring transition-colors duration-100 outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
                        selected
                          ? "bg-accent-soft inset-ring-accent-line"
                          : "inset-ring-line-2 hover:bg-fill-1"
                      )}
                    >
                      <VendorLogo vendor={option.value} />
                      <span className="text-base font-medium text-fg">
                        {option.label}
                      </span>
                    </button>
                  );
                })}
              </div>

              {hasSelectedVendor ? (
                <>
                  <Tabs
                    value={providerType}
                    onValueChange={(value) =>
                      setProviderType(value === "saml" ? "saml" : "oidc")
                    }
                  >
                    <TabsList aria-label="Provider Type">
                      <TabsTrigger value="oidc">OIDC</TabsTrigger>
                      <TabsTrigger value="saml">SAML</TabsTrigger>
                    </TabsList>
                  </Tabs>

                  {selectedVendor === "custom" ? (
                    <Field
                      id={providerIdFieldId}
                      label="Provider ID (Advanced)"
                      help="Internal identifier used to create the tenant provider config. Most firms won’t need to change this."
                    >
                      <Input
                        id={providerIdFieldId}
                        aria-describedby={`${providerIdFieldId}-help`}
                        placeholder={
                          providerType === "oidc"
                            ? "oidc.my-idp"
                            : "saml.my-idp"
                        }
                        value={providerId}
                        onChange={(e) => setProviderId(e.target.value)}
                      />
                    </Field>
                  ) : null}

                  <Field
                    id={labelId}
                    label="Label"
                    help="Displayed on the login button."
                  >
                    <Input
                      id={labelId}
                      aria-describedby={`${labelId}-help`}
                      placeholder={
                        selectedVendor === "azure"
                          ? "Microsoft Entra ID"
                          : selectedVendor === "google"
                          ? "Google Workspace"
                          : selectedVendor === "onelogin"
                          ? "OneLogin"
                          : selectedVendor === "custom"
                          ? "SSO"
                          : "Okta"
                      }
                      value={providerLabel}
                      onChange={(e) => setProviderLabel(e.target.value)}
                    />
                  </Field>

                  {providerType === "oidc" ? (
                    <>
                      <Field
                        id={issuerId}
                        label="Issuer"
                        help="Example: https://example.okta.com/oauth2/default"
                      >
                        <Input
                          id={issuerId}
                          aria-describedby={`${issuerId}-help`}
                          placeholder="https://idp.example.com/oauth2/default"
                          value={oidcIssuer}
                          onChange={(e) => setOidcIssuer(e.target.value)}
                        />
                      </Field>
                      <Field id={clientIdId} label="Client ID">
                        <Input
                          id={clientIdId}
                          placeholder="0oa..."
                          value={oidcClientId}
                          onChange={(e) => setOidcClientId(e.target.value)}
                        />
                      </Field>
                      <Field
                        id={clientSecretId}
                        label="Client Secret"
                        help="Your data is protected and encrypted. We take your security seriously."
                      >
                        <Input
                          id={clientSecretId}
                          aria-describedby={`${clientSecretId}-help`}
                          type="password"
                          placeholder="••••••••"
                          value={oidcClientSecret}
                          onChange={(e) => setOidcClientSecret(e.target.value)}
                        />
                      </Field>
                    </>
                  ) : (
                    <>
                      <div className="flex flex-col gap-1.5">
                        <Label htmlFor={metadataUrlId}>
                          Import From Metadata
                        </Label>
                        <div className="flex items-center gap-2">
                          <Input
                            id={metadataUrlId}
                            placeholder="https://your-idp.com/metadata.xml"
                            value={samlMetadataUrl}
                            onChange={(e) => setSamlMetadataUrl(e.target.value)}
                          />
                          <Button
                            disabled={isLoading || !samlMetadataUrl.trim()}
                            onClick={handleImportSamlMetadata}
                          >
                            Fetch
                          </Button>
                        </div>
                      </div>

                      <div className="flex items-center gap-3">
                        <Separator className="w-auto flex-1" />
                        <span className="text-sm text-fg-3">
                          Or Enter Manually
                        </span>
                        <Separator className="w-auto flex-1" />
                      </div>

                      <div className="flex flex-col gap-1.5">
                        <Label>Binding Type</Label>
                        <Tabs
                          value={samlBindingPreference}
                          onValueChange={(value) =>
                            setSamlBindingPreference(
                              value === "post" ? "post" : "redirect"
                            )
                          }
                        >
                          <TabsList aria-label="Binding Type">
                            <TabsTrigger value="post">HTTP-POST</TabsTrigger>
                            <TabsTrigger value="redirect">
                              HTTP-Redirect
                            </TabsTrigger>
                          </TabsList>
                        </Tabs>
                      </div>

                      <Field
                        id={idpEntityIdId}
                        label="IdP Entity ID (Issuer)"
                        help="The unique identifier of your Identity Provider."
                      >
                        <Input
                          id={idpEntityIdId}
                          aria-describedby={`${idpEntityIdId}-help`}
                          placeholder="e.g., http://www.okta.com/exk..."
                          value={samlIdpEntityId}
                          onChange={(e) => setSamlIdpEntityId(e.target.value)}
                        />
                      </Field>
                      <Field
                        id={ssoUrlId}
                        label="SSO Login URL"
                        help="The URL where users will be redirected to authenticate."
                      >
                        <Input
                          id={ssoUrlId}
                          aria-describedby={`${ssoUrlId}-help`}
                          placeholder="https://..."
                          value={samlSsoUrl}
                          onChange={(e) => setSamlSsoUrl(e.target.value)}
                        />
                      </Field>

                      <div className="flex flex-col gap-3">
                        <span className="text-base font-medium text-fg">
                          Certificate
                        </span>
                        <div className="flex items-center justify-between gap-3 rounded-lg bg-fill-1 px-3 py-2.5">
                          <div className="flex min-w-0 flex-col gap-0.5">
                            <span className="truncate text-base text-fg">
                              {samlCertFileName ??
                                "Upload X.509 certificate (.pem/.crt/.cer)"}
                            </span>
                            <span className="text-sm text-fg-3">
                              Public certificate only (no private key).
                            </span>
                          </div>
                          <input
                            ref={samlCertFileRef}
                            type="file"
                            accept=".pem,.crt,.cer,.txt"
                            className="hidden"
                            onChange={(e) => {
                              const f = e.target.files?.[0] ?? null;
                              handleUploadCertFile(f).catch(() => {});
                            }}
                          />
                          <Button
                            disabled={isLoading}
                            onClick={() => samlCertFileRef.current?.click()}
                          >
                            Upload
                          </Button>
                        </div>

                        <Field
                          id={certId}
                          label="X.509 Certificate"
                          help="Paste the public certificate provided by your IdP."
                        >
                          <Textarea
                            id={certId}
                            aria-describedby={`${certId}-help`}
                            className="font-mono text-sm"
                            placeholder="-----BEGIN CERTIFICATE-----\n...\n-----END CERTIFICATE-----"
                            value={samlCert}
                            onChange={(e) => setSamlCert(e.target.value)}
                          />
                        </Field>
                      </div>

                      <div className="flex flex-col gap-3 rounded-lg bg-fill-1 p-3">
                        <span className="text-base font-medium text-fg">
                          Service Provider Values
                        </span>
                        <CopyValue
                          label="Callback URL (ACS)"
                          value={samlCallbackUrl}
                        />
                        <CopyValue
                          label="SP Entity ID / Audience URI"
                          value={previewSamlSpEntityId}
                          placeholder={previewSamlSpEntityIdMessage}
                        />
                      </div>
                    </>
                  )}

                  <div className="flex items-center justify-end gap-2">
                    <Button onClick={() => goToStep("tenant")}>Back</Button>
                    <Button
                      variant="primary"
                      disabled={isLoading}
                      onClick={() => handleUpsertProvider().catch(() => {})}
                    >
                      Save and Continue
                      {continueIcon}
                    </Button>
                  </div>
                </>
              ) : null}
            </>
          ) : (
            <span className="text-base text-fg">
              {providerDone ? "Provider configured" : "Not configured"}
            </span>
          )}
        </StepSection>

        {/* Step 4: Provision */}
        <StepSection
          title="Step 4: Provision Users"
          description="Migrate existing users to SSO"
          done={provisionDone}
          current={wizardStep === "provision"}
          locked={!providerDone && wizardStep !== "provision"}
          onNavigate={navigateTo("provision")}
        >
          {!providerDone && wizardStep !== "provision" ? (
            <LockedNote>Complete Step 3 to continue</LockedNote>
          ) : wizardStep === "provision" ? (
            <>
              <p className="text-base text-fg-3">
                This creates tenant users with the same UID/email as your
                existing CaseBlink lawyer records.
              </p>
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  disabled={isLoading}
                  onClick={() => handleProvision(true)}
                >
                  Dry Run
                </Button>
                <Button
                  variant="primary"
                  disabled={isLoading}
                  onClick={() => handleProvision(false)}
                >
                  Provision Now
                </Button>
                {hasProvisioned ? (
                  <StatusBadge tone="success">
                    <Check />
                    Provisioning Complete
                  </StatusBadge>
                ) : null}
              </div>
            </>
          ) : (
            <span className="text-base text-fg">
              {provisionDone ? "Provisioning complete" : "Not provisioned"}
            </span>
          )}
        </StepSection>

        {/* Step 5: Enable */}
        <StepSection
          title="Step 5: Enable SSO"
          description="IT instructions and cutover"
          done={enableDone}
          doneLabel="Enabled"
          current={wizardStep === "enable"}
          locked={!provisionDone && wizardStep !== "enable"}
          onNavigate={navigateTo("enable")}
        >
          {!provisionDone && wizardStep !== "enable" ? (
            <LockedNote>Complete Step 4 to continue</LockedNote>
          ) : wizardStep === "enable" ? (
            <>
              <div className="flex flex-col gap-3 rounded-lg bg-fill-1 p-3">
                <p className="text-base text-fg">
                  Before enabling SSO, we recommend validating the configuration
                  to ensure your configuration is correct.
                </p>
                <div className="flex flex-wrap items-center gap-3">
                  <Button
                    disabled={isLoading || !configuredProvider}
                    onClick={() =>
                      handleValidateConfiguration().catch(() => {})
                    }
                  >
                    <PlayCircle />
                    Validate Configuration
                  </Button>
                  <StatusBadge tone="neutral">
                    <Clock />
                    Invite-Only Provisioning Enforced
                  </StatusBadge>
                </div>
              </div>

              <div className="flex flex-col gap-3 rounded-lg bg-fill-1 p-3">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-base font-medium text-fg">
                    What to Send IT (Copy/Paste)
                  </span>
                  <CopyButton
                    value={itInstructionsText}
                    label="Copy IT Instructions"
                  />
                </div>
                <CopyValue
                  label="1) Redirect / Callback URL"
                  value={redirectUrl}
                />
                {isSamlConfigured ? (
                  <CopyValue
                    label="2) SP Entity ID / Audience URI"
                    value={configuredSamlSpEntityId}
                  />
                ) : null}
                <CopyValue
                  label={
                    isSamlConfigured ? "3) App Login URL" : "2) App Login URL"
                  }
                  value={appLoginUrl}
                />
                <CopyValue
                  label={
                    isSamlConfigured
                      ? "4) Allowed Email Domain"
                      : "3) Allowed Email Domain"
                  }
                  value={normalizedDomains.join(", ")}
                  placeholder="(none set)"
                />
              </div>

              <div className="flex items-center justify-end gap-2">
                <Button onClick={() => goToStep("provision")}>Back</Button>
                <Button
                  variant="primary"
                  disabled={isLoading}
                  onClick={() => handleEnable().catch(() => {})}
                >
                  {isLoading ? <Spinner className="size-3.5" /> : null}
                  Enable SSO
                </Button>
              </div>
            </>
          ) : (
            <span className="text-base text-fg">
              {enableDone ? "SSO enabled" : "Ready to enable"}
            </span>
          )}
        </StepSection>
      </SettingsColumn>
    </TooltipProvider>
  );
}
